import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';

import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '../../../components';
import { VALUES_CONFIG_INFO } from '../infoDoc';
import { useGeneratorsStyles, useStyles } from '../styles';
import { HelmConfiguration } from '../types';
import {
  validateSourceGitURL,
  validateSourceRevision,
  validateValueFilePattern,
} from '../validation';
import { RepoUrlPicker } from './RepoUrlPicker';

const MAX_VALUE_FILES = 20;

function deriveValuesObjectText(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

export interface HelmValuesConfigProps {
  helm: HelmConfiguration;
  onChange: (value: HelmConfiguration) => void;
  enableAppSetSupport: boolean;
  errors: string[];
}

export const HelmValuesConfig = ({
  helm,
  onChange,
  enableAppSetSupport,
  errors,
}: HelmValuesConfigProps) => {
  const { classes } = useStyles();
  const { classes: generatorClasses } = useGeneratorsStyles();
  const theme = useTheme();

  const valuesSourceEnabled = Boolean(helm.valuesSource);
  const valuesObjectText = deriveValuesObjectText(helm.valuesObject);

  const handleValueFileAdd = () => {
    if (helm.valueFiles.length >= MAX_VALUE_FILES) return;
    onChange({ ...helm, valueFiles: [...helm.valueFiles, ''] });
  };

  const handleValueFileChange = (index: number, v: string) => {
    const next = [...helm.valueFiles];
    next[index] = v;
    onChange({ ...helm, valueFiles: next });
  };

  const handleValueFileRemove = (index: number) => {
    onChange({
      ...helm,
      valueFiles: helm.valueFiles.filter((_, i) => i !== index),
    });
  };

  const handleValuesSourceToggle = (enabled: boolean) => {
    if (enabled) {
      onChange({
        ...helm,
        valuesSource: helm.valuesSource ?? {
          repoURL: '',
          targetRevision: '',
        },
      });
    } else {
      const { valuesSource: _drop, ...rest } = helm;
      onChange(rest);
    }
  };

  const handleValuesSourceField = (
    field: 'repoURL' | 'targetRevision',
    v: string,
  ) => {
    const existing = helm.valuesSource ?? { repoURL: '', targetRevision: '' };
    onChange({ ...helm, valuesSource: { ...existing, [field]: v } });
  };

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Values Configuration"
          description="Define where Helm values come from and how they're applied."
          infoDoc={VALUES_CONFIG_INFO}
        />
      </Box>

      <Box className={classes.inputContainer}>
        <FormControlLabel
          control={
						<Switch
							color="secondary"
              checked={valuesSourceEnabled}
              onChange={e => handleValuesSourceToggle(e.target.checked)}
            />
          }
          label="Use a separate Git repository for value files"
        />
      </Box>

      {valuesSourceEnabled && (
        <Box>
          <Box className={classes.inputContainer}>
            <RepoUrlPicker
              value={helm.valuesSource?.repoURL ?? ''}
              onChange={url => handleValuesSourceField('repoURL', url)}
              label="Git URL"
              required
              freeSolo={false}
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceGitURL(helm.valuesSource?.repoURL ?? '')
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceGitURL(helm.valuesSource?.repoURL ?? '') ? (
                  <ScaffolderFieldError message="Git URL must be a TomTom Git repository (github.com/tomtom-internal/* or tomtom-international/*)." />
                ) : (
                  'URL of the TomTom Git repository containing the value files.'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label="Git Revision"
              required
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={helm.valuesSource?.targetRevision ?? ''}
              onChange={e =>
                handleValuesSourceField('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(helm.valuesSource?.targetRevision ?? '')
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(
                  helm.valuesSource?.targetRevision ?? '',
                ) ? (
                  <ScaffolderFieldError message="Revision must be a valid Git branch, tag, or commit hash (letters, numbers, dots, hyphens, underscores, or slashes)." />
                ) : (
                  'The Git revision of the values source (branch, tag, or commit hash).'
                )
              }
            />
          </Box>
        </Box>
      )}

      {helm.valueFiles.map((vf, index) => (
        <Box key={index} className={classes.flexContainer}>
          <Box className={classes.inputContainer}>
            <TextField
              label="Value File"
              variant="outlined"
              margin="none"
              size="small"
              fullWidth
              value={vf}
              onChange={e => handleValueFileChange(index, e.target.value)}
              slotProps={{
                htmlInput: { 'aria-label': `Value File ${index + 1}` },
              }}
              error={
                Boolean(errors?.length > 0) &&
                (!vf || !validateValueFilePattern(vf, { enableAppSetSupport }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!vf ||
                  !validateValueFilePattern(vf, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message="Value file path must contain only letters, numbers, dots, hyphens, underscores, asterisks, or slashes." />
                ) : undefined
              }
            />
          </Box>
          <Box className={classes.removeButtonContainer}>
            <Button
              variant="outlined"
							size="small"
              color="secondary"
              aria-label={`Remove Value File ${index + 1}`}
              onClick={() => handleValueFileRemove(index)}
            >
              Remove
            </Button>
          </Box>
        </Box>
      ))}

      <Box className={classes.inputContainer}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
					onClick={handleValueFileAdd}
          color="secondary"
          disabled={helm.valueFiles.length >= MAX_VALUE_FILES}
        >
          Add Value File
        </Button>
      </Box>

      <Box className={classes.inputContainer}>
        <FormControlLabel
          control={
            <Checkbox
							checked={helm.ignoreMissingValueFiles}
              color="secondary"
              onChange={e =>
                onChange({
                  ...helm,
                  ignoreMissingValueFiles: e.target.checked,
                })
              }
            />
          }
          label="Ignore Missing Value Files"
        />
      </Box>

      {enableAppSetSupport &&
        (helm.valuesObject !== undefined ? (
          <>
            <Box className={classes.inputContainer}>
              <ScaffolderInputHeader
                title="Values Overrides (YAML)"
                description="For Helm values that reference ApplicationSet generator variables (e.g. image: '{{ .head_sha }}'). Static values belong in value files."
              />
            </Box>
            <Box margin={1} marginBottom={2}>
              <Box className={generatorClasses.codeMirror} sx={{ height: 200 }}>
                <CodeMirror
                  value={valuesObjectText}
                  extensions={[yamlLang()]}
                  theme={
                    theme.palette.mode === 'dark' ? githubDark : githubLight
                  }
                  onChange={v =>
                    onChange({
                      ...helm,
                      valuesObject: v as unknown as Record<string, unknown>,
                    })
                  }
                />
              </Box>
              <Box mt={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => onChange({ ...helm, valuesObject: undefined })}
                >
                  Remove Values Overrides
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Box className={classes.inputContainer}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  ...helm,
                  valuesObject: '' as unknown as Record<string, unknown>,
                })
              }
            >
              Add Values Overrides
            </Button>
          </Box>
        ))}
    </Box>
  );
};
